import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Globe, FileText, ExternalLink, Edit, Trash2, Rocket, MoreHorizontal } from 'lucide-react';
import EmptyState from '../components/EmptyState';
import { apiRequest } from '../config/api';
import { useToast } from '../context/ToastContext';
import SkeletonSiteDetail from '../components/skeletons/SkeletonSiteDetail';

function timeAgo(dateString) {
  const now = new Date();
  const date = new Date(dateString);
  const seconds = Math.floor((now - date) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days !== 1 ? 's' : ''} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months !== 1 ? 's' : ''} ago`;
  const years = Math.floor(months / 12);
  return `${years} year${years !== 1 ? 's' : ''} ago`;
}

function PageActionsDropdown({ page, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(!open);
        }}
        className="p-1.5 rounded-md text-gray-300 hover:text-gray-500 hover:bg-gray-50 transition-colors"
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-44 origin-top-right rounded-lg bg-white shadow-lg border border-gray-100 py-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(page.id);
              setOpen(false);
            }}
            className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Edit className="h-3.5 w-3.5 mr-2.5 text-gray-400" />
            Edit
          </button>
          <div className="my-1 border-t border-gray-100" />
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(page.id);
              setOpen(false);
            }}
            className="flex items-center w-full px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
          >
            <Trash2 className="h-3.5 w-3.5 mr-2.5" />
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

export default function SiteDetail() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const [site, setSite] = useState(null);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddPageModal, setShowAddPageModal] = useState(false);
  const [newPageTitle, setNewPageTitle] = useState('');
  const [creatingPage, setCreatingPage] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const modalRef = useRef(null);

  const fetchSite = useCallback(async () => {
    try {
      const response = await apiRequest(`/sites/${siteId}`);
      if (response.ok) {
        const data = await response.json();
        setSite(data);
      } else {
        throw new Error('Failed to fetch site');
      }
    } catch (error) {
      console.error('Error fetching site:', error);
      showError('Failed to load site');
    }
  }, [siteId, showError]);

  const fetchPages = useCallback(async () => {
    try {
      const response = await apiRequest(`/sites/${siteId}/pages`);
      if (response.ok) {
        const data = await response.json();
        setPages(data);
      } else {
        throw new Error('Failed to fetch pages');
      }
    } catch (error) {
      console.error('Error fetching pages:', error);
      showError('Failed to load pages');
    }
  }, [siteId, showError]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchSite(), fetchPages()]);
      setLoading(false);
    };
    loadData();
  }, [fetchSite, fetchPages]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        setShowAddPageModal(false);
        setNewPageTitle('');
      }
    }
    if (showAddPageModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAddPageModal]);

  const handleAddPage = async (e) => {
    e.preventDefault();
    if (!newPageTitle.trim()) return;

    try {
      setCreatingPage(true);
      const response = await apiRequest('/pages/', {
        method: 'POST',
        body: JSON.stringify({
          title: newPageTitle,
          page_type: 'landing',
          content_type: 'tff_page_builder_json_simple',
          content: {},
          site_id: siteId,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create page');
      }

      const newPage = await response.json();
      setShowAddPageModal(false);
      setNewPageTitle('');
      showSuccess('Page created successfully');
      navigate(`/pages/${newPage.id}/builder/`);
    } catch (error) {
      console.error('Error creating page:', error);
      showError('Failed to create page');
    } finally {
      setCreatingPage(false);
    }
  };

  const handleDeletePage = async (pageId) => {
    if (!window.confirm('Are you sure you want to delete this page? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await apiRequest(`/pages/${pageId}/`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setPages(prev => prev.filter(p => p.id !== pageId));
        showSuccess('Page deleted successfully');
      } else {
        throw new Error('Failed to delete page');
      }
    } catch (error) {
      console.error('Error deleting page:', error);
      showError('Failed to delete page');
    }
  };

  const handleDeploy = async () => {
    try {
      setDeploying(true);
      const response = await apiRequest(`/sites/${siteId}/deploy`, {
        method: 'POST',
      });

      if (response.ok) {
        const updatedSite = await response.json();
        setSite(updatedSite);
        showSuccess('Site deployment started');
      } else {
        throw new Error('Failed to deploy site');
      }
    } catch (error) {
      console.error('Error deploying site:', error);
      showError('Failed to deploy site');
    } finally {
      setDeploying(false);
    }
  };

  const handlePublishToggle = async () => {
    if (!site) return;

    const action = site.is_published ? 'unpublish' : 'publish';

    try {
      const response = await apiRequest(`/sites/${siteId}/${action}`, {
        method: 'POST',
      });

      if (response.ok) {
        const updatedSite = await response.json();
        setSite(updatedSite);
        showSuccess(`Site ${action}ed successfully`);
      } else {
        throw new Error(`Failed to ${action} site`);
      }
    } catch (error) {
      console.error(`Error ${action}ing site:`, error);
      showError(`Failed to ${action} site`);
    }
  };

  if (loading) return <SkeletonSiteDetail />;

  if (!site) {
    return (
      <div className="px-8 py-8 max-w-6xl mx-auto">
        <Link to="/sites" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors mb-6">
          <ArrowLeft className="h-4 w-4" />
          Back to Sites
        </Link>
        <div className="text-center py-20">
          <p className="text-sm text-gray-500">Site not found</p>
        </div>
      </div>
    );
  }

  const deploymentStatusLabel = {
    not_deployed: 'Not Deployed',
    deploying: 'Deploying',
    deployed: 'Deployed',
    failed: 'Deploy Failed',
  };

  const deploymentStatusColor = {
    not_deployed: 'bg-gray-100 text-gray-500',
    deploying: 'bg-yellow-50 text-yellow-700',
    deployed: 'bg-green-50 text-green-700',
    failed: 'bg-red-50 text-red-700',
  };

  return (
    <div className="px-8 py-8 max-w-6xl mx-auto">
      {/* Back link */}
      <Link to="/sites" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors mb-6">
        <ArrowLeft className="h-4 w-4" />
        Back to Sites
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">{site.name}</h1>
          {site.subdomain && (
            <div className="flex items-center gap-1.5 mt-1.5">
              <Globe className="h-3.5 w-3.5 text-gray-400" />
              <span className="text-sm text-gray-500">{site.subdomain}.fasttttt.com</span>
              {site.deployment_status === 'deployed' && (
                <a
                  href={`https://${site.subdomain}.fasttttt.com`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-brand-600 transition-colors"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
            </div>
          )}
          <div className="flex items-center gap-2 mt-3">
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                site.is_published
                  ? 'bg-brand-50 text-brand-700'
                  : 'bg-gray-100 text-gray-500'
              }`}
            >
              {site.is_published ? 'Published' : 'Draft'}
            </span>
            {site.deployment_status && site.deployment_status !== 'not_deployed' && (
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                  deploymentStatusColor[site.deployment_status] || 'bg-gray-100 text-gray-500'
                }`}
              >
                {deploymentStatusLabel[site.deployment_status] || site.deployment_status}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handlePublishToggle}
            className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              site.is_published
                ? 'text-gray-600 bg-gray-100 hover:bg-gray-200'
                : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
            }`}
          >
            {site.is_published ? 'Unpublish' : 'Publish'}
          </button>
          {site.subdomain && (
            <button
              onClick={handleDeploy}
              disabled={deploying || !site.subdomain}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-medium rounded-lg text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50 transition-colors"
            >
              <Rocket className="h-4 w-4" />
              {deploying ? 'Deploying...' : 'Deploy'}
            </button>
          )}
        </div>
      </div>

      {/* Pages section */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <h2 className="text-base font-semibold text-gray-900">Pages</h2>
          <span className="text-sm text-gray-400">{pages.length}</span>
          <button
            onClick={() => setShowAddPageModal(true)}
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg text-white bg-brand-600 hover:bg-brand-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add Page
          </button>
        </div>

        {pages.length === 0 ? (
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
            <EmptyState
              illustration="pages"
              headline="No pages yet"
              description="Add your first page to start building this site."
              buttonLabel="Add Page"
              onAction={() => setShowAddPageModal(true)}
            />
          </div>
        ) : (
          <div>
            {/* Column headers */}
            <div className="grid grid-cols-[1fr_160px_100px_44px] items-center px-4 pb-2 border-b border-gray-200">
              <span className="text-xs font-medium text-gray-500">Name</span>
              <span className="text-xs font-medium text-gray-500">Last edited</span>
              <span className="text-xs font-medium text-gray-500">Type</span>
              <span className="sr-only">Actions</span>
            </div>

            {/* Rows */}
            <div>
              {pages.map((page) => (
                <Link
                  key={page.id}
                  to={`/pages/${page.id}/builder/`}
                  className="grid grid-cols-[1fr_160px_100px_44px] items-center px-4 py-3 border-b border-gray-100 hover:bg-gray-50/50 transition-colors group"
                >
                  {/* Name */}
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-900 truncate group-hover:text-brand-600 transition-colors">
                          {page.title}
                        </p>
                        {page.is_homepage && (
                          <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium bg-brand-50 text-brand-700 flex-shrink-0">
                            Home
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Last edited */}
                  <div className="text-sm text-gray-500">
                    {timeAgo(page.updated_at || page.created_at)}
                  </div>

                  {/* Type */}
                  <div className="text-sm text-gray-500 capitalize">
                    {page.page_type || 'landing'}
                  </div>

                  {/* Actions */}
                  <div onClick={(e) => e.preventDefault()}>
                    <PageActionsDropdown
                      page={page}
                      onEdit={(id) => navigate(`/pages/${id}/builder/`)}
                      onDelete={handleDeletePage}
                    />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Add Page Modal */}
      {showAddPageModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div
            ref={modalRef}
            className="w-full max-w-sm mx-4 bg-white rounded-xl shadow-lg border border-gray-100 p-6"
          >
            <h3 className="text-base font-semibold text-gray-900">Add New Page</h3>
            <p className="mt-1 text-sm text-gray-500">Give your page a name to get started.</p>
            <form onSubmit={handleAddPage} className="mt-5">
              <input
                type="text"
                value={newPageTitle}
                onChange={(e) => setNewPageTitle(e.target.value)}
                placeholder="Page title"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-gray-400 transition-colors"
                disabled={creatingPage}
                autoFocus
              />
              <div className="flex items-center justify-end gap-2 mt-5 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddPageModal(false);
                    setNewPageTitle('');
                  }}
                  className="px-3.5 py-1.5 text-sm text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={creatingPage}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!newPageTitle.trim() || creatingPage}
                  className="px-3.5 py-1.5 text-sm font-medium bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {creatingPage ? 'Creating...' : 'Create Page'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
